
import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  GraduationCap,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  User,
} from "lucide-react";

import { api } from "../lib/api";

/* ==========================================
   INITIAL FORM VALUES
========================================== */

const initialFormData = () => ({
  name: "",
  email: "",
  phone: "",
  address: "",
  class: "",
  message: "",
  agreeTerms: false,
  receiveUpdates: false,
  updateChannels: {
    email: false,
    sms: false,
    whatsapp: false,
    rcs: false,
  },
});

const classes = [
  "Bal Vatika",
  "LKG",
  "UKG",
  "1st",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th",
  "11th",
  "12th",
];

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-[#FFFCFA] px-4 py-3 text-[13px] font-normal text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:ring-2 focus:ring-red-100";

const labelClass =
  "mb-1.5 block text-[13px] font-medium text-slate-700";

/* ==========================================
   INPUT COMPONENT
========================================== */

function FormInput({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  icon: Icon,
  error,
  required = false,
  maxLength,
}) {
  return (
    <div className="min-w-0">
      <label
        htmlFor={`enquiry-${name}`}
        className={labelClass}
      >
        {label}

        {required && (
          <span className="ml-1 text-red-600">*</span>
        )}
      </label>

      <div className="relative">
        <Icon
          size={17}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-red-700"
        />

        <input
          id={`enquiry-${name}`}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          required={required}
          aria-invalid={Boolean(error)}
          className={`${inputClass} pl-10 ${
            error ? "border-red-500" : ""
          }`}
        />
      </div>

      {error && (
        <p
          role="alert"
          className="mt-1.5 flex items-center gap-1 text-xs text-red-600"
        >
          <AlertCircle size={13} />
          {error}
        </p>
      )}
    </div>
  );
}

/* ==========================================
   CONTACT INFORMATION
========================================== */

function ContactItem({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-800">
        <Icon size={18} />
      </div>

      <div className="min-w-0 pt-0.5">
        <p className="text-[11px] font-normal text-slate-500">
          {label}
        </p>

        <div className="mt-1 break-words text-[13px] font-normal leading-6 text-slate-800">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ==========================================
   MAIN COMPONENT
========================================== */

export default function Enquiry() {
  const [formData, setFormData] = useState(
    initialFormData
  );

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [isSubmitted, setIsSubmitted] =
    useState(false);

  const [submitStatus, setSubmitStatus] =
    useState(null);

  const [submitError, setSubmitError] =
    useState("");

  /* ==========================================
     INPUT HANDLER
  ========================================== */

  function handleChange(event) {
    const { name, value, type, checked } =
      event.target;

    if (
      type === "checkbox" &&
      name.startsWith("channel_")
    ) {
      const channel = name.replace("channel_", "");

      setFormData((previous) => ({
        ...previous,
        updateChannels: {
          ...previous.updateChannels,
          [channel]: checked,
        },
      }));
    } else if (type === "checkbox") {
      setFormData((previous) => ({
        ...previous,
        [name]: checked,
      }));
    } else {
      setFormData((previous) => ({
        ...previous,
        [name]: value,
      }));
    }

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));

    setSubmitStatus(null);
    setSubmitError("");
  }

  /* ==========================================
     VALIDATION
  ========================================== */

  function validateForm() {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        formData.email.trim()
      )
    ) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (
      !/^\d{10}$/.test(formData.phone.trim())
    ) {
      newErrors.phone =
        "Enter a valid 10-digit phone number.";
    }

    if (!formData.class) {
      newErrors.class = "Please select a class.";
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms =
        "Please agree to the Terms & Conditions.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }

  /* ==========================================
     SUBMIT ENQUIRY
  ========================================== */

  async function handleSubmit(event) {
    event.preventDefault();

    if (isSubmitting || !validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitError("");

    try {
      await api.submitEnquiry({
        fullName: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        className: formData.class,
        subject: "Admission Enquiry",
        message: formData.message.trim(),
        receiveUpdates: formData.receiveUpdates,
        updateChannels: formData.updateChannels,
        agreeTerms: formData.agreeTerms,
        source: "admission",
      });

      setSubmitStatus("success");
      setIsSubmitted(true);
      setErrors({});
    } catch (error) {
      setSubmitStatus("error");

      setSubmitError(
        error?.message ||
          "Failed to send your enquiry. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function resetForm() {
    setFormData(initialFormData());
    setErrors({});
    setIsSubmitted(false);
    setSubmitStatus(null);
    setSubmitError("");
  }

  /* ==========================================
     KEYBOARD & ACCESSIBILITY
  ========================================== */

  useEffect(() => {
    if (!isSubmitted) return;

    function handleEscape(event) {
      if (event.key === "Escape") {
        resetForm();
      }
    }

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [isSubmitted]);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-14">

      {/* BACKGROUND DECORATION */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-28 top-[350px] h-80 w-80 rounded-full bg-red-100/50 blur-3xl" />

        <div className="absolute -right-28 bottom-0 h-80 w-80 rounded-full bg-amber-100/50 blur-3xl" />
      </div>

      {/* ======================================
          PAGE HERO
      ====================================== */}

      <section className="relative overflow-hidden bg-gradient-to-r from-red-900 via-red-800 to-red-700 px-4 pb-24 pt-11 text-white sm:px-6 lg:px-8">

        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[36px] border-white/5" />

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="relative mx-auto max-w-6xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/10 px-4 py-2 text-[11px] font-normal tracking-wide text-amber-100">
            <Sparkles size={15} />
            Admissions & Enquiries
          </span>

          <h1 className="mt-5 font-serif text-[31px] font-medium leading-tight sm:text-[41px] lg:text-[48px]">
            Admission{" "}
            <span className="text-amber-300">
              Enquiry
            </span>
          </h1>

          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-amber-400" />

          <p className="mx-auto mt-4 max-w-xl text-[13px] font-normal leading-6 text-white/85 sm:text-[14px]">
            Have questions about admission? Share your
            details and our school team will get in touch.
          </p>
        </motion.div>
      </section>

      {/* ======================================
          MAIN CONTENT
      ====================================== */}

      <div className="relative z-10 mx-auto -mt-11 max-w-6xl px-4 sm:px-6 lg:px-8">

        <div className="grid items-start gap-6 lg:grid-cols-[0.87fr_1.65fr]">

          {/* ======================================
              LEFT SIDE - SCHOOL DETAILS
          ====================================== */}

          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
            className="space-y-5"
          >

            {/* SCHOOL INFO CARD */}
            <div className="overflow-hidden rounded-[22px] border border-red-100 bg-white shadow-[0_12px_35px_rgba(80,25,25,0.07)]">

              {/* CARD HEADER */}
              <div className="relative overflow-hidden bg-gradient-to-r from-red-900 to-red-700 px-5 py-5 text-white">

                <div className="pointer-events-none absolute -right-6 -top-10 h-28 w-28 rounded-full border-[17px] border-white/10" />

                <div className="relative flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
                    <GraduationCap size={24} />
                  </div>

                  <div>
                    <h2 className="font-serif text-[19px] font-medium leading-snug">
                      St. Xavier&apos;s Public School
                    </h2>

                    <p className="mt-1 text-xs text-white/75">
                      Bela, Cholapur, Varanasi
                    </p>
                  </div>
                </div>
              </div>

              {/* CONTACT DETAILS */}
              <div className="space-y-5 p-5">

                <ContactItem
                  icon={MapPin}
                  label="School Address"
                >
                  Bela, Cholapur, Varanasi,
                  Uttar Pradesh - 221101
                </ContactItem>

                <ContactItem
                  icon={Phone}
                  label="Phone Numbers"
                >
                  <div className="flex flex-col">
                    <a
                      href="tel:+918601017877"
                      className="transition hover:text-red-800"
                    >
                      +91 86010 17877
                    </a>

                    <a
                      href="tel:+919450785369"
                      className="transition hover:text-red-800"
                    >
                      +91 94507 85369
                    </a>
                  </div>
                </ContactItem>

                <ContactItem
                  icon={Mail}
                  label="Email Address"
                >
                  <a
                    href="mailto:st.xavierbelavns@gmail.com"
                    className="break-all transition hover:text-red-800"
                  >
                    st.xavierbelavns@gmail.com
                  </a>
                </ContactItem>

              </div>

              <div className="h-[3px] bg-gradient-to-r from-red-800 via-red-500 to-amber-400" />
            </div>

            {/* SUPPORT CARD */}
            <div className="rounded-[20px] border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-red-50 p-5 shadow-sm">

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <MessageCircle size={20} />
                </div>

                <h3 className="font-serif text-lg font-medium text-slate-900">
                  Need Assistance?
                </h3>
              </div>

              <p className="mt-3 text-[13px] leading-6 text-slate-600">
                Our school team is available to assist
                you with admission-related enquiries.
              </p>

              <a
                href="tel:+918601017877"
                className="mt-4 inline-flex items-center gap-2 text-[13px] font-medium text-red-800 transition hover:text-red-600"
              >
                <Phone size={16} />
                Call the School Office
                <ArrowRight size={15} />
              </a>
            </div>

          </motion.aside>

          {/* ======================================
              RIGHT SIDE - ENQUIRY FORM
          ====================================== */}

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="overflow-hidden rounded-[22px] border border-red-100 bg-white shadow-[0_16px_45px_rgba(80,25,25,0.09)]"
          >

            {/* FORM HEADER */}
            <div className="flex items-center gap-3 border-b border-red-100 bg-gradient-to-r from-red-50 via-white to-amber-50 px-5 py-5 sm:px-6">

              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-800 text-white">
                <Send size={20} />
              </div>

              <div>
                <h2 className="font-serif text-xl font-medium text-slate-900">
                  Admission Enquiry Form
                </h2>

                <p className="mt-1 text-xs font-normal text-slate-500">
                  Fill in your details to request admission information.
                </p>
              </div>
            </div>

            {/* SUCCESS MESSAGE */}
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="submitted"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  role="status"
                  className="px-6 py-16 text-center sm:px-10"
                >
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-green-200 bg-green-50">
                    <CheckCircle2
                      size={40}
                      className="text-green-600"
                    />
                  </div>

                  <h3 className="mt-5 font-serif text-2xl font-medium text-slate-900">
                    Enquiry Submitted
                  </h3>

                  <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-slate-600">
                    Thank you for your interest in
                    St. Xavier&apos;s Public School.
                    Your enquiry has been submitted
                    successfully. Our school team
                    will contact you.
                  </p>

                  <button
                    type="button"
                    onClick={resetForm}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-3 text-sm font-medium text-white transition hover:bg-red-900"
                  >
                    Submit Another Enquiry
                    <ArrowRight size={16} />
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="enquiry-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  noValidate
                  className="space-y-5 p-5 sm:p-6"
                >

                  {/* FORM ERROR */}
                  {submitStatus === "error" && (
                    <div
                      role="alert"
                      className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm leading-6 text-red-700"
                    >
                      <AlertCircle
                        size={18}
                        className="mt-0.5 shrink-0"
                      />

                      <span>
                        {submitError ||
                          "Failed to send enquiry. Please try again."}
                      </span>
                    </div>
                  )}

                  {/* NAME */}
                  <FormInput
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    icon={User}
                    error={errors.name}
                    required
                  />

                  {/* EMAIL & PHONE */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormInput
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      icon={Mail}
                      error={errors.email}
                      required
                    />

                    <FormInput
                      label="Phone Number"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10-digit mobile number"
                      icon={Phone}
                      error={errors.phone}
                      maxLength={10}
                      required
                    />
                  </div>

                  {/* ADDRESS & CLASS */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormInput
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter your address"
                      icon={MapPin}
                    />

                    <div>
                      <label
                        htmlFor="enquiry-class"
                        className={labelClass}
                      >
                        Class Seeking Admission
                        <span className="ml-1 text-red-600">
                          *
                        </span>
                      </label>

                      <div className="relative">
                        <GraduationCap
                          size={18}
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-red-700"
                        />

                        <select
                          id="enquiry-class"
                          name="class"
                          value={formData.class}
                          onChange={handleChange}
                          aria-invalid={Boolean(errors.class)}
                          className={`${inputClass} appearance-none pl-10 pr-10 ${
                            errors.class
                              ? "border-red-500"
                              : ""
                          }`}
                        >
                          <option value="">
                            Select Class
                          </option>

                          {classes.map((className) => (
                            <option
                              key={className}
                              value={className}
                            >
                              {className}
                            </option>
                          ))}
                        </select>

                        <ChevronDown
                          size={17}
                          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                        />
                      </div>

                      {errors.class && (
                        <p
                          role="alert"
                          className="mt-1.5 flex items-center gap-1 text-xs text-red-600"
                        >
                          <AlertCircle size={13} />
                          {errors.class}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* MESSAGE */}
                  <div>
                    <label
                      htmlFor="enquiry-message"
                      className={labelClass}
                    >
                      Message{" "}
                      <span className="font-normal text-slate-400">
                        (Optional)
                      </span>
                    </label>

                    <textarea
                      id="enquiry-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Enter your question or message..."
                      className={`${inputClass} resize-y`}
                    />
                  </div>

                  {/* COMMUNICATION PREFERENCES */}
                  <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">

                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        name="receiveUpdates"
                        checked={formData.receiveUpdates}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 shrink-0 accent-red-800"
                      />

                      <span className="text-[13px] font-normal leading-6 text-slate-700">
                        I would like to receive school updates
                        through my preferred communication
                        channels.
                      </span>
                    </label>

                    <AnimatePresence>
                      {formData.receiveUpdates && (
                        <motion.div
                          initial={{
                            opacity: 0,
                            height: 0,
                          }}
                          animate={{
                            opacity: 1,
                            height: "auto",
                          }}
                          exit={{
                            opacity: 0,
                            height: 0,
                          }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3 border-t border-amber-200/70 pt-4">
                            {[
                              ["email", "Email"],
                              ["sms", "SMS"],
                              ["whatsapp", "WhatsApp"],
                              ["rcs", "RCS"],
                            ].map(([key, label]) => (
                              <label
                                key={key}
                                className="flex cursor-pointer items-center gap-2 text-xs text-slate-700"
                              >
                                <input
                                  type="checkbox"
                                  name={`channel_${key}`}
                                  checked={
                                    formData.updateChannels[key]
                                  }
                                  onChange={handleChange}
                                  className="h-4 w-4 accent-red-800"
                                />

                                {label}
                              </label>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* TERMS & CONDITIONS */}
                  <div>
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 shrink-0 accent-red-800"
                      />

                      <span className="text-[12px] font-normal leading-6 text-slate-600">
                        I agree to the{" "}
                        <a
                          href="/terms-of-use"
                          className="text-red-800 underline-offset-2 hover:underline"
                        >
                          Terms & Conditions
                        </a>{" "}
                        and{" "}
                        <a
                          href="/privacy-policy"
                          className="text-red-800 underline-offset-2 hover:underline"
                        >
                          Privacy Policy
                        </a>
                        .
                      </span>
                    </label>

                    {errors.agreeTerms && (
                      <p
                        role="alert"
                        className="mt-2 flex items-center gap-1.5 text-xs text-red-600"
                      >
                        <AlertCircle size={13} />
                        {errors.agreeTerms}
                      </p>
                    )}
                  </div>

                  {/* SUBMIT BUTTON */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-900 to-red-700 px-5 py-3.5 text-sm font-medium text-white shadow-md shadow-red-900/10 transition hover:from-red-800 hover:to-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Submitting Enquiry...
                      </>
                    ) : (
                      <>
                        <Send size={17} />
                        Submit Admission Enquiry

                        <ArrowRight
                          size={17}
                          className="transition-transform group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </button>

                  {/* PRIVACY NOTE */}
                  <div className="flex items-center justify-center gap-2 text-center">
                    <ShieldCheck
                      size={15}
                      className="shrink-0 text-amber-700"
                    />

                    <p className="text-[11px] font-normal text-slate-500">
                      Your details will be used to respond
                      to your admission enquiry.
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </main>
  );
}

